import React, { useRef, useState, useEffect, useCallback } from 'react';
import './CustomTooltip.scss';

export type TooltipPosition = 'top' | 'bottom';

interface CustomTooltipProps {
  content: string | React.ReactNode;
  children: React.ReactElement;
  position?: TooltipPosition;
  maxWidth?: string;
  className?: string;
}

const calculateTooltipPosition = (triggerRect: DOMRect, tooltipRect: DOMRect, position: TooltipPosition) => {
  const left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
  const top = position === 'top' 
    ? triggerRect.top - tooltipRect.height - 8 
    : triggerRect.bottom + 8;

  return { left, top };
};

export const CustomTooltip: React.FC<CustomTooltipProps> = ({
  content,
  children,
  position = 'top',
  maxWidth,
  className = '',
}) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current || !tooltipRef.current) return;

    const trigger = triggerRef.current.getBoundingClientRect();
    const tooltip = tooltipRef.current.getBoundingClientRect();
    
    const { left, top } = calculateTooltipPosition(trigger, tooltip, position);

    tooltipRef.current.style.left = `${left}px`;
    tooltipRef.current.style.top = `${top}px`;
  }, [position]);

  const handleMouseEnter = useCallback(() => {
    setIsVisible(true);
    requestAnimationFrame(updatePosition);
  }, [updatePosition]);

  const handleMouseLeave = useCallback(() => {
    setIsVisible(false);
  }, []);

  useEffect(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    trigger.addEventListener('mouseenter', handleMouseEnter);
    trigger.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      trigger.removeEventListener('mouseenter', handleMouseEnter);
      trigger.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [handleMouseEnter, handleMouseLeave]);

  useEffect(() => {
    if (!isVisible) return;

    const hideOnScroll = () => setIsVisible(false);
    
    window.addEventListener('scroll', hideOnScroll, true);
    return () => window.removeEventListener('scroll', hideOnScroll, true);
  }, [isVisible]);

  const arrowClass = position === 'top' ? 'custom-tooltip-balloon--bottom-arrow' : 'custom-tooltip-balloon--top-arrow';

  return (
    <div
      className={`custom-tooltip-wrapper ${className}`.trim()} 
      ref={triggerRef}
    >
      {children}

      <div 
        ref={tooltipRef}
        className={`custom-tooltip-balloon ${arrowClass} ${isVisible ? 'is-visible' : ''}`}
        style={{ maxWidth }}
        role="tooltip"
      >
        {content}
      </div>
    </div>
  );
};